import React, { useMemo } from 'react';
import { MapPin, Building2, Compass, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  getAllStates,
  getLgasForState,
  getAreasForLga,
  getStateByName,
  isValidLgaForState,
} from '../../data/nigerianLocations';

export interface LocationSelectorProps {
  idPrefix?: string;
  selectedState: string;
  selectedLga: string;
  selectedCityArea?: string;
  onStateChange: (state: string) => void;
  onLgaChange: (lga: string) => void;
  onCityAreaChange?: (cityArea: string) => void;
  showCityArea?: boolean;
  showCountry?: boolean;
  showCapitalHint?: boolean;
  disabled?: boolean;
  required?: boolean;
  layout?: 'grid' | 'stacked' | 'horizontal';
  stateLabel?: string;
  lgaLabel?: string;
  cityAreaLabel?: string;
  theme?: 'dark' | 'light';
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  idPrefix = 'loc',
  selectedState,
  selectedLga,
  selectedCityArea = '',
  onStateChange,
  onLgaChange,
  onCityAreaChange,
  showCityArea = true,
  showCountry = true,
  showCapitalHint = true,
  disabled = false,
  required = true,
  layout = 'grid',
  stateLabel = 'State / FCT',
  lgaLabel = 'Local Government Area (LGA)',
  cityAreaLabel = 'City / Town / Area',
  theme = 'dark',
}) => {
  const allStates = useMemo(() => getAllStates(), []);

  // Compute LGAs for currently selected state
  const availableLgas = useMemo(() => {
    if (!selectedState) return [];
    return getLgasForState(selectedState);
  }, [selectedState]);

  // Compute known areas/neighborhoods for currently selected LGA
  const availableAreas = useMemo(() => {
    if (!selectedState || !selectedLga) return [];
    return getAreasForLga(selectedState, selectedLga);
  }, [selectedState, selectedLga]);

  // Get state metadata for capital info
  const stateMeta = useMemo(() => {
    return getStateByName(selectedState);
  }, [selectedState]);

  // Handle State Change: clear dependent LGA and City/Area
  const handleStateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newState = e.target.value;
    onStateChange(newState);
    onLgaChange('');
    if (onCityAreaChange) {
      onCityAreaChange('');
    }
  };

  // Handle LGA Change: clear dependent City/Area if changed
  const handleLgaSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLga = e.target.value;
    onLgaChange(newLga);
    if (onCityAreaChange) {
      onCityAreaChange('');
    }
  };

  // Handle City/Area Change
  const handleAreaSelect = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    if (onCityAreaChange) {
      onCityAreaChange(e.target.value);
    }
  };

  const isDark = theme === 'dark';
  const labelClasses = `block text-xs font-bold uppercase tracking-wider mb-1.5 ${
    isDark ? 'text-slate-300' : 'text-slate-700'
  }`;
  const inputClasses = `w-full rounded-xl px-3.5 py-2.5 text-xs font-medium transition-all focus:outline-none focus:ring-2 cursor-pointer ${
    isDark
      ? 'bg-slate-950 border border-slate-800 text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20'
      : 'bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-emerald-600 focus:ring-emerald-600/20 shadow-xs'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  const isLgaValid = selectedState && selectedLga ? isValidLgaForState(selectedState, selectedLga) : true;

  const gridClass =
    layout === 'grid'
      ? showCityArea && showCountry
        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'
        : showCityArea || showCountry
        ? 'grid grid-cols-1 sm:grid-cols-3 gap-3'
        : 'grid grid-cols-1 sm:grid-cols-2 gap-3'
      : layout === 'horizontal'
      ? 'flex flex-wrap items-center gap-3'
      : 'space-y-3';

  return (
    <div id={`${idPrefix}-location-container`} className="space-y-2">
      <div className={gridClass}>
        {/* Level 0: Country */}
        {showCountry && (
          <div id={`${idPrefix}-country-group`} className="w-full">
            <label htmlFor={`${idPrefix}-country-select`} className={labelClasses}>
              Country
            </label>
            <div className="relative">
              <select
                id={`${idPrefix}-country-select`}
                disabled
                className={`${inputClasses} appearance-none pr-8 bg-slate-900/50 cursor-default`}
                value="Nigeria"
              >
                <option value="Nigeria">Nigeria (Federal Republic)</option>
              </select>
              <span className="absolute right-3 top-2.5 text-xs pointer-events-none">🇳🇬</span>
            </div>
          </div>
        )}

        {/* Level 1: State / FCT */}
        <div id={`${idPrefix}-state-group`} className="w-full">
          <label htmlFor={`${idPrefix}-state-select`} className={labelClasses}>
            {stateLabel} {required && <span className="text-emerald-500">*</span>}
          </label>
          <div className="relative">
            <select
              id={`${idPrefix}-state-select`}
              value={selectedState}
              onChange={handleStateSelect}
              disabled={disabled}
              required={required}
              className={`${inputClasses} appearance-none pr-8`}
            >
              <option value="">Select State / FCT</option>
              {allStates.map((st) => (
                <option key={st.id} value={st.name}>
                  {st.name} {st.isFct ? '(Federal Capital)' : ''}
                </option>
              ))}
            </select>
            <MapPin className={`w-4 h-4 absolute right-3 top-3 pointer-events-none ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
          </div>
        </div>

        {/* Level 2: LGA / Area Council */}
        <div id={`${idPrefix}-lga-group`} className="w-full">
          <label htmlFor={`${idPrefix}-lga-select`} className={labelClasses}>
            {lgaLabel} {required && <span className="text-emerald-500">*</span>}
          </label>
          <div className="relative">
            <select
              id={`${idPrefix}-lga-select`}
              value={selectedLga}
              onChange={handleLgaSelect}
              disabled={disabled || !selectedState || availableLgas.length === 0}
              required={required}
              className={`${inputClasses} appearance-none pr-8`}
            >
              <option value="">
                {!selectedState
                  ? 'First select a State'
                  : `Select LGA (${availableLgas.length} available)`}
              </option>
              {availableLgas.map((lga) => (
                <option key={lga} value={lga}>
                  {lga}
                </option>
              ))}
            </select>
            <Building2 className={`w-4 h-4 absolute right-3 top-3 pointer-events-none ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
          </div>
        </div>

        {/* Level 3: City / Town / Area */}
        {showCityArea && onCityAreaChange && (
          <div id={`${idPrefix}-city-area-group`} className="w-full">
            <label htmlFor={`${idPrefix}-city-area-select`} className={labelClasses}>
              {cityAreaLabel}
            </label>
            <div className="relative">
              {availableAreas.length > 0 ? (
                <select
                  id={`${idPrefix}-city-area-select`}
                  value={selectedCityArea}
                  onChange={handleAreaSelect}
                  disabled={disabled || !selectedLga}
                  className={`${inputClasses} appearance-none pr-8`}
                >
                  <option value="">Select or leave as general {selectedLga || 'LGA'}</option>
                  {availableAreas.map((area) => (
                    <option key={area.id} value={area.name}>
                      {area.name} {area.isCapitalCity ? '★ (State Capital)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id={`${idPrefix}-city-area-input`}
                  type="text"
                  placeholder={selectedLga ? `e.g. Town, district, or estate in ${selectedLga}` : 'Select LGA first'}
                  value={selectedCityArea}
                  onChange={handleAreaSelect}
                  disabled={disabled || !selectedLga}
                  className={inputClasses}
                />
              )}
              <Compass className={`w-4 h-4 absolute right-3 top-3 pointer-events-none ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            </div>
          </div>
        )}
      </div>

      {/* Helper Bar: Capital Info & Validation check */}
      {showCapitalHint && selectedState && stateMeta && (
        <div className={`flex flex-wrap items-center justify-between text-[11px] px-1 pt-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-emerald-500">Official Capital:</span>
            <span className={isDark ? 'text-slate-200 font-medium' : 'text-slate-800 font-medium'}>
              {stateMeta.capital}
            </span>
            <span className="text-slate-500">• Zone: {stateMeta.zone}</span>
          </div>

          {selectedLga && isLgaValid && (
            <div className="flex items-center gap-1 text-emerald-500 font-medium">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>Valid Nigerian Administrative LGA</span>
            </div>
          )}

          {selectedLga && !isLgaValid && (
            <div className="flex items-center gap-1 text-rose-400 font-medium">
              <AlertCircle className="w-3.5 h-3.5" />
              <span>LGA does not belong to {selectedState}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
